import { FC } from 'react';
import {
  Box,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import type { Cut, Extra, FreeSelectionQuestion, Trim } from '../../types';
import { formatPricing } from '../../utils/pricing';
import { getAllItems, getItemSpecifications } from './wizard-utils';
import { SpecFieldsGrid } from './spec-fields-grid';
import { useQuestionnaireResponse } from './questionnaire-response-context';

interface FreeSelectionWizardProps {
  question: FreeSelectionQuestion;
}

/**
 * Renders the interactive body for a FreeSelection question.
 * Items can be toggled individually; selecting an item expands its spec inputs.
 * @param root0
 * @param root0.question
 */
export const FreeSelectionWizard: FC<FreeSelectionWizardProps> = ({
  question,
}) => {
  const {
    catalog,
    answers,
    itemSpecValues,
    handleAnswerChange,
    handleSpecChange,
  } = useQuestionnaireResponse();
  const answer = answers[question.id] ?? '';
  const specValues = itemSpecValues[question.id] ?? {};

  // Multi-select is the default; only explicit `false` flips to single-select.
  // Matches the seeding/validation convention in questionnaire-response-context
  // so a null/undefined value from legacy records doesn't disagree with itself.
  const isMultiSelect = question.allowMultiple !== false;

  const allItems = getAllItems(catalog);
  const availableItems = question.availableCutIds
    .map((id) => allItems.find((item) => item.id === id))
    .filter(Boolean) as (Cut | Trim | Extra)[];

  const selectedItems = isMultiSelect
    ? (answer as string[]) || []
    : (answer as string) || '';

  const handleToggle = (itemId: string) => {
    if (isMultiSelect) {
      const current = (selectedItems as string[]) || [];
      const next = current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId];
      handleAnswerChange(question.id, next);
    } else {
      handleAnswerChange(question.id, itemId);
    }
  };

  return (
    <Stack spacing={2}>
      {availableItems.map((item) => {
        const isSelected = isMultiSelect
          ? Array.isArray(selectedItems) && selectedItems.includes(item.id)
          : selectedItems === item.id;

        const assignedSpecs = getItemSpecifications(catalog, item.id);
        const hasSpecs = assignedSpecs.length > 0;

        return (
          <Card
            key={item.id}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderWidth: 2,
              outline: '2px solid transparent',
              transition: 'all 0.3s ease',
              overflow: 'hidden',
              minHeight: item.imageUrl ? { xs: 120, md: 160 } : 80,
              '&:hover': {
                outline: '2px solid',
                outlineColor: isSelected ? 'primary.main' : 'divider',
              },
            }}
          >
            <Stack
              direction="row"
              onClick={() => handleToggle(item.id)}
              sx={{
                cursor: 'pointer',
                minHeight: item.imageUrl ? { xs: 120, md: 160 } : 80,
                transition: 'min-height 0.3s ease',
              }}
            >
              {/* Image */}
              {item.imageUrl && (
                <Box
                  sx={{
                    width: { xs: 120, md: 160 },
                    minWidth: { xs: 120, md: 160 },
                    alignSelf: 'stretch',
                    position: 'relative',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={item.imageUrl}
                    alt={item.title || ''}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}

              {/* Name / Description / Specs */}
              <Stack
                sx={{ flex: 1, minWidth: 0, p: 2 }}
                justifyContent="flex-start"
                spacing={0.5}
              >
                {/* Title row with checkbox */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h5">
                    {item.title || 'Unnamed'}
                  </Typography>
                  {isMultiSelect ? (
                    <Checkbox
                      checked={isSelected}
                      readOnly
                      sx={{ pointerEvents: 'none' }}
                    />
                  ) : (
                    <Radio
                      checked={isSelected}
                      readOnly
                      sx={{ pointerEvents: 'none' }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>

                {item.pricing && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ fontWeight: 'medium' }}
                  >
                    {formatPricing(item.pricing)}
                  </Typography>
                )}
                <Collapse in={isSelected && hasSpecs} unmountOnExit>
                  <Divider sx={{ my: 1 }} />
                  <SpecFieldsGrid
                    specs={assignedSpecs}
                    values={specValues[item.id] || {}}
                    onChange={(specId, v) =>
                      handleSpecChange(question.id, item.id, specId, v)
                    }
                  />
                </Collapse>
              </Stack>
            </Stack>
          </Card>
        );
      })}

      {isMultiSelect && question.maxSelections && (
        <Typography variant="caption" color="text.secondary">
          {`Select up to ${question.maxSelections} items`}
        </Typography>
      )}
    </Stack>
  );
};
